/**
 * Utility functions to manage MediaSession API and prevent system media notifications
 * on mobile devices during avatar voice playback.
 */

/**
 * Disables media session notifications by setting metadata to null and playback state to 'none'
 * This prevents system media notifications from appearing on mobile devices
 */
export const disableMediaSession = (): void => {
 if ("mediaSession" in navigator) {
  try {
   navigator.mediaSession.metadata = null;
   navigator.mediaSession.playbackState = "none";

   // Clear any existing action handlers that might trigger notifications
   navigator.mediaSession.setActionHandler("play", null);
   navigator.mediaSession.setActionHandler("pause", null);
   navigator.mediaSession.setActionHandler("stop", null);
   navigator.mediaSession.setActionHandler("seekbackward", null);
   navigator.mediaSession.setActionHandler("seekforward", null);
   navigator.mediaSession.setActionHandler("seekto", null);
   navigator.mediaSession.setActionHandler("previoustrack", null);
   navigator.mediaSession.setActionHandler("nexttrack", null);
  } catch (error) {}
 }
};

/**
 * Resets media session to default state
 */
export const resetMediaSession = (): void => {
 disableMediaSession();
};

/**
 * Sets up media session for avatar voice playback with disabled notifications
 * @param audioElement - The HTMLAudioElement being used for playback
 */
export const setupAvatarAudioMediaSession = (
 audioElement: HTMLAudioElement
): void => {
 if (!("mediaSession" in navigator)) return;

 try {
  // Disable notifications
  disableMediaSession();

  // Add event listeners to maintain disabled state
  const handlePlay = () => disableMediaSession();
  const handlePause = () => disableMediaSession();
  const handleEnded = () => disableMediaSession();

  audioElement.addEventListener("play", handlePlay);
  audioElement.addEventListener("pause", handlePause);
  audioElement.addEventListener("ended", handleEnded);

  // Store cleanup function on the audio element
  (audioElement as any)._mediaSessionCleanup = () => {
   audioElement.removeEventListener("play", handlePlay);
   audioElement.removeEventListener("pause", handlePause);
   audioElement.removeEventListener("ended", handleEnded);
   disableMediaSession();
  };
 } catch (error) {}
};

/**
 * Cleans up media session handlers from an audio element
 * @param audioElement - The HTMLAudioElement to clean up
 */
export const cleanupAvatarAudioMediaSession = (
 audioElement: HTMLAudioElement
): void => {
 if ((audioElement as any)._mediaSessionCleanup) {
  (audioElement as any)._mediaSessionCleanup();
  delete (audioElement as any)._mediaSessionCleanup;
 }
};
